'use client';

import { useEffect, useRef } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { EntityProcedureCollapseToggle } from './_components/entity-procedure-collapse-toggle.component';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion.component';
import { EntityProcedureScheduleCardProps } from '@/app/(main)/(pages)/_components/entities/entity-procedure/entity-procedure-schedule-card.type';
import { cn } from '@/utils/cn-tw-merge';

const TRIGGER_STATE_STYLES = [
  '[&[data-state=closed]_[data-procedure-toggle]]:bg-gray-200',
  '[&[data-state=open]_[data-procedure-toggle]]:bg-white',
  '[&[data-state=closed]:hover_[data-procedure-toggle]]:bg-gray-300',
  '[&[data-state=open]:hover_[data-procedure-toggle]]:bg-gray-50',
  '[&[data-state=open]_[data-procedure-toggle]]:rotate-180',
];

export function EntityProcedureScheduleCard({
  accordionKey,
  title,
  children,
  defaultOpen = false,
  onOpenChange,
  className,
}: EntityProcedureScheduleCardProps) {
  const handleValueChange = (value: string[]) => onOpenChange?.(value.includes(accordionKey));

  const hasReportedInitialState = useRef(false);

  useEffect(() => {
    if (hasReportedInitialState.current) {
      return;
    }

    hasReportedInitialState.current = true;
    onOpenChange?.(defaultOpen);
  }, [defaultOpen, onOpenChange]);

  return (
    <Accordion
      type="multiple"
      defaultValue={defaultOpen ? [accordionKey] : []}
      onValueChange={handleValueChange}
      className={className}
      data-testid={DataTestIdEnum.ENTITY_PROCEDURE_SCHEDULE_CARD}
    >
      <AccordionItem
        value={accordionKey}
        className="rounded-3xl border border-gray-300 bg-white px-3 py-6 transition-colors data-[state=open]:bg-green-200 xl:px-6"
      >
        <AccordionTrigger
          showNoIcon
          underlineOnHover={false}
          className={cn(
            'w-full items-start gap-2 rounded-lg text-start',
            'outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2',
            TRIGGER_STATE_STYLES,
          )}
          data-testid={DataTestIdEnum.ENTITY_PROCEDURE_SCHEDULE_CARD_TRIGGER}
        >
          <span className="flex-1 text-base font-bold text-gray-700 [word-break:break-word]">{title}</span>
          <EntityProcedureCollapseToggle />
        </AccordionTrigger>

        <AccordionContent className="flex flex-col gap-4 pt-4">
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
